import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Html, HtmlProps } from './Html';
import { Markdown, parseMarkdownMultimediaType, MultimediaType, MarkdownProps } from '@slope/uikit';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { BrokenIcon, CloseIcon, ImageIcon, TextIcon, VideoIcon } from '@slope/icons';
import { VideoPlaceholder } from './components';

const iconsMapping = {
  [MultimediaType.Image]: ImageIcon,
  [MultimediaType.Video]: VideoIcon,
  [MultimediaType.Heading]: TextIcon,
  [MultimediaType.Text]: TextIcon,
};

const toVector3 = (
  mixed:
    | THREE.Vector3
    | THREE.Vector3Tuple
    | number
    | Readonly<THREE.Vector3 | THREE.Vector3Tuple | number>,
): THREE.Vector3 => {
  if (Array.isArray(mixed)) {
    return new THREE.Vector3(mixed[0], mixed[1], mixed[2]);
  }
  if (typeof mixed === 'number') {
    return new THREE.Vector3(mixed, mixed, mixed);
  }
  // @ts-expect-error it should be a THREE.Vector3
  return new THREE.Vector3(mixed.x, mixed.y, mixed.z);
};

export type AnnotationProps = Omit<HtmlProps, 'children'> & {
  children: string;
};

const useForceUpdate = () => {
  const [, setTick] = useState(0);
  return () => setTick((tick) => tick + 1);
};
/**
 * Annotation component for rendering annotations in a 3D scene.
 *
 * This component consists in a three state system:
 * 1. Marker - a 3D object that represents the annotation in the scene.
 * 2. Thumbnail - a 2D image that represents the annotation in the UI.
 * 3. Modal - Where the content of the annotation is displayed in fullscreen.
 */
export const Annotation = React.forwardRef<HTMLDivElement, AnnotationProps>(
  ({ children, position, visible, ...restProps }, forwardedRef) => {
    const invalidate = useThree(({ invalidate }) => invalidate);
    const [isVisible, setIsVisible] = useState(true);
    const controls = useThree(({ controls }) => controls) as unknown as
      | {
          enabled: boolean;
        }
      | undefined;
    const forceUpdate = useForceUpdate();
    const wrapperRef = useRef<HTMLElement>(null);
    const lastModeRef = useRef<'marker' | 'thumbnail' | 'modal'>('marker');
    const modeRef = useRef<'marker' | 'thumbnail' | 'modal'>('marker');
    const {
      data: { multimedia },
    } = useMemo(() => parseMarkdownMultimediaType(children), [children]);
    const camera = useThree((state) => state.camera);

    const updateMode = (newMode: 'marker' | 'thumbnail' | 'modal') => {
      if (lastModeRef.current !== modeRef.current) {
        forceUpdate();
        invalidate();
        lastModeRef.current = modeRef.current;
      }
      modeRef.current = newMode;
    };

    useFrame(() => {
      if (modeRef.current === 'modal') {
        return null;
      }
      const distance = camera.position.distanceTo(toVector3(position || [0, 0, 0]));
      if (distance < 100) {
        updateMode('thumbnail');
      } else {
        updateMode('marker');
      }

      if (distance > 250) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isVisible && setIsVisible(false);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !isVisible && setIsVisible(true);
      }
    });

    const handleClick = () => {
      console.log('Annotation clicked, current mode:', modeRef.current);
      updateMode('modal');
    };

    useEffect(() => {
      if (!controls) return;

      controls.enabled = modeRef.current !== 'modal';
    }, [controls, modeRef.current]);

    let style = {
      userSelect: 'none',
      display: 'flex',
      // prettier-ignore
      fontFamily: '\'Nunito Sans\', sans-serif',
      boxSizing: 'border-box',
      //padding: '10px',
      //width: '300px',
      boxShadow: '0 0 5px rgba(0, 0, 0, 0.5)',
      borderRadius: modeRef.current === 'marker' ? '100%' : '5px',
      overflow: 'hidden',
      opacity: visible !== false && isVisible ? 1 : 0,
      backgroundColor: 'rgba(240, 240, 240, 0.8)',
      transformOrigin: 'center',
      color: '#353434',
      transition: 'opacity, transform, scale, padding, border-radius, width 0.2s ease-in-out',
    } as React.CSSProperties;

    if (modeRef.current === 'modal') {
      style = {
        ...style,
        width: '100vw',
        overflow: 'hidden auto',
        maxWidth: '400px',
        maxHeight: 'calc(100vh - 120px)',
        padding: '8px',
      };
    } else {
      style = {
        ...style,
        overflow: 'hidden',
        objectFit: 'cover',
        padding: modeRef.current === 'marker' ? '4px' : '0px',
        width: modeRef.current === 'marker' ? '32px' : '160px',
        height: modeRef.current === 'marker' ? '32px' : '90px',
        //aspectRatio: modeRef.current === 'marker' ? '1 / 1' : '16 / 9',
        scale: modeRef.current === 'marker' ? '0.75' : '1',
        alignItems: 'center',
        justifyContent: 'center',
      };
    }

    const node = useMemo(() => {
      let markdownProps: MarkdownProps = {
        videoConfig: {
          properties: {
            autoPlay: true,
            playsInline: true,
          },
        },
      };
      switch (modeRef.current) {
        case 'marker': {
          return multimedia?.type && iconsMapping[multimedia.type] ? (
            React.createElement(iconsMapping[multimedia.type], {
              style: { cursor: 'pointer' },
              onClick: handleClick,
            })
          ) : (
            <BrokenIcon />
          );
        }
        case 'thumbnail': {
          switch (multimedia?.type) {
            case MultimediaType.Image: {
              return (
                <img
                  style={{ cursor: 'pointer' }}
                  onClick={handleClick}
                  src={multimedia.src}
                  alt="Thumbnail"
                />
              );
            }
            case MultimediaType.Video: {
              markdownProps = {
                components: {
                  video: VideoPlaceholder,
                },
                allowElement: (element, index) => {
                  console.log('Allow element:', element, index);
                  return (
                    element.type === 'element' && ['video', 'source'].includes(element.tagName)
                  );
                },
              };
              break;
            }
            case MultimediaType.Heading:
            case MultimediaType.Text: {
              markdownProps = {
                ...markdownProps,
                components: {
                  video: VideoPlaceholder,
                },
                allowElement: (element) => {
                  return element.type === 'element' && /^(h[1-6]|p|span)$/i.test(element.tagName);
                },
              };
            }
          }
          break;
        }
      }

      return markdownProps ? (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            width: '100%',
            height: '100%',
            cursor: modeRef.current !== 'modal' ? 'pointer' : undefined,
          }}
          onClick={modeRef.current !== 'modal' ? handleClick : undefined}
        >
          <button
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              visibility: modeRef.current === 'modal' ? 'visible' : 'hidden',
            }}
            onClick={() => updateMode('marker')}
          >
            <CloseIcon />
          </button>
          <Markdown {...markdownProps}>{children}</Markdown>
        </div>
      ) : (
        <BrokenIcon />
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children, multimedia, modeRef.current, handleClick]);

    useEffect(() => {
      if (modeRef.current === 'modal' && wrapperRef.current) {
        wrapperRef.current.style.transform = 'translate(50vw, 50vh)';
        wrapperRef.current.style.transition = 'transform 0.2s ease-in-out';
        wrapperRef.current.style.zIndex = '2000';
      } else if (wrapperRef.current) {
        wrapperRef.current.style.transition = null!;
        wrapperRef.current.style.zIndex = null!;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modeRef.current === 'modal']);

    return (
      <Html
        ref={forwardedRef}
        style={style}
        //occlude={modeRef.current === 'marker' ? 'blending' : undefined}
        //transform
        //sprite={true}
        center
        pointerEvents="none"
        position={position}
        geometry={modeRef.current === 'marker' ? <circleGeometry args={[0.2, 8, 8]} /> : undefined}
        //position-y={modeRef.current === 'thumbnail' ? 0.5 : 0 + position?.[1] || 0}
        wrapperTransform={modeRef.current === 'modal' ? false : undefined}
        wrapperRef={wrapperRef}
        {...restProps}
      >
        {node}
      </Html>
    );
  },
);
